// src/pages/ExamSessions/ExamSessionEdit.jsx
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useExamSessions } from '../../hooks/useExamSessions';
import ExamSessionForm from '../../components/forms/ExamSessionForm';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const ExamSessionEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { examSessions, updateExamSession } = useExamSessions();

  const [loading, setLoading] = useState(false);
  const [sessionRow, setSessionRow] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const found = examSessions.find((s) => s.id === id);
    if (found) {
      setSessionRow(found);
    } else if (examSessions.length > 0) {
      // sessions loaded but this id not in list
      setNotFound(true);
    }
  }, [id, examSessions]);

  const handleSubmit = async (formData) => {
    setLoading(true);
    try {
      const result = await updateExamSession(id, formData);
      if (result.success) {
        alert('Exam session updated successfully!');
        navigate('/exam-sessions');
      } else {
        alert(result.error || 'Failed to update exam session');
      }
    } catch (e) {
      console.error('Error updating exam session:', e);
      alert('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/exam-sessions');
  };

  if (notFound) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={handleCancel}
            className="flex items-center"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-1" />
            Back
          </Button>
          <h1 className="text-xl font-bold text-gray-900">Exam Session Not Found</h1>
        </div>
        <Card>
          <div className="py-8 text-center">
            <p className="text-gray-500">
              The exam session you are trying to edit does not exist.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  if (!sessionRow) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={handleCancel}
            className="flex items-center"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-1" />
            Back
          </Button>
          <h1 className="text-xl font-bold text-gray-900">Edit Exam Session</h1>
        </div>
        <Card>
          <div className="py-8 text-center">
            <p className="text-gray-500">Loading exam session data...</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <Button
          variant="outline"
          onClick={handleCancel}
          className="flex items-center"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-1" />
          Back
        </Button>
        <h1 className="text-xl font-bold text-gray-900">Edit Exam Session</h1>
      </div>

      <Card>
        <ExamSessionForm
          initialData={sessionRow}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          loading={loading}
        />
      </Card>
    </div>
  );
};

export default ExamSessionEdit;
